'use client';

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getStrapiFormatUrl } from '@/lib/strapi-media-url';
import ContactOfficeCard from './ContactOfficeCard';
import type { ContactWonderOffice } from '@/types/contact';

interface ContactOfficeSectionProps {
  section: ContactWonderOffice;
}

export default function ContactOfficeSection({ section }: ContactOfficeSectionProps) {
  const groupRef = useRef<HTMLDivElement>(null);
  const cardIconUrl = getStrapiFormatUrl(section.commonCardIcon);
  const emailIconUrl = getStrapiFormatUrl(section.commonEmailIcon);
  const phoneIconUrl = getStrapiFormatUrl(section.commonPhoneIcon);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const cards = groupRef.current?.querySelectorAll('[data-office-card]');
      if (!cards) return;

      const heading = groupRef.current?.querySelector('[data-office-heading]');
      if (heading) {
        gsap.fromTo(
          heading,
          { autoAlpha: 0, y: 16 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: groupRef.current,
              start: 'top 85%',
              once: true,
            } as ScrollTrigger.Vars,
          },
        );
      }

      gsap.fromTo(
        cards,
        { autoAlpha: 0, y: 28 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.55,
          ease: 'power3.out',
          stagger: 0.08,
          scrollTrigger: { trigger: groupRef.current, start: 'top 80%', once: true },
        },
      );
    }, groupRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={groupRef} className="bg-[#f3f3f3]">
      <div className="mx-auto container max-sm:px-4">
        <div data-office-heading className="mb-6" style={{ opacity: 0 }}>
          {section.eyebrowHeading ? (
            <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.28em] text-[#E31E24]">
              {section.eyebrowHeading}
            </p>
          ) : null}
          {section.heading ? (
            <h2 className="text-[1.5rem] font-bold tracking-tight text-stone-900 sm:text-[1.75rem]">
              {section.heading}
            </h2>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(section.officeCards ?? []).map((office) => (
            <ContactOfficeCard
              key={office.id}
              office={office}
              cardIconUrl={cardIconUrl}
              emailIconUrl={emailIconUrl}
              phoneIconUrl={phoneIconUrl}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
